import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Eye, EyeOff, Lock, Loader2, ShieldCheck } from "lucide-react";
import { changePassword } from "../ApiHelper/url_Helpers";
import { toasterSuccess, toasterError } from "./toaster";

const PasswordField = ({ label, name, show, setShow, formik, ...props }) => (
  <div className="space-y-1">
    <label className="text-sm font-semibold text-gray-700">{label}</label>
    <div className="relative group">
      <span className="absolute left-3 top-3 text-gray-400">
        <Lock size={18} />
      </span>
      <input
        type={show ? "text" : "password"}
        name={name}
        {...formik.getFieldProps(name)}
        className={`w-full pl-10 pr-12 py-2.5 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
          formik.touched[name] && formik.errors[name]
            ? "border-red-500 focus:ring-red-200"
            : "border-gray-200 focus:ring-cyan-100 focus:border-[#00B8D9]"
        }`}
        placeholder={`Enter your ${label.toLowerCase()}`}
        {...props}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 transition-colors"
      >
        {show ? <Eye size={18} /> : <EyeOff  size={18} />}
      </button>
    </div>
    {formik.touched[name] && formik.errors[name] && (
      <p className="text-red-500 text-xs mt-1">{formik.errors[name]}</p>
    )}
  </div>
);

const ChangePasswordForm = () => {
  const [loading, setLoading] = useState(false);
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Current password is required"),
      newPassword: Yup.string()
        .min(8, "New password must be at least 8 characters")
        .matches(
          /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/,
          "Password must contain at least one letter and one number"
        )
        .notOneOf([Yup.ref('currentPassword')], "New password cannot be the same as current password")
        .required("New password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      try {
        await changePassword({
          old_password: values.currentPassword,
          new_password: values.newPassword,
          confirm_password: values.confirmPassword,
        });
        toasterSuccess("Password changed successfully!");
        resetForm();
      } catch (error) {
        toasterError(error?.message || "Failed to change password");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col items-center mb-6">
        <div className="p-3 bg-cyan-100 rounded-full text-[#00B8D9] mb-3">
          <ShieldCheck size={32} />
        </div>
        <h3 className="text-lg font-bold text-gray-800">Change Password</h3>
        <p className="text-sm text-gray-500">Update your account password securely.</p>
      </div>

      <form onSubmit={formik.handleSubmit} className="max-w-md mx-auto space-y-5">
        <PasswordField
            label="Current Password"
            name="currentPassword"
            show={showCurrentPass}
            setShow={setShowCurrentPass}
            formik={formik}
        />

        <PasswordField
            label="New Password"
            name="newPassword"
            show={showNewPass}
            setShow={setShowNewPass}
            formik={formik}
        />

        <PasswordField
            label="Confirm Password"
            name="confirmPassword"
            show={showConfirmPass}
            setShow={setShowConfirmPass}
            formik={formik}
        />

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading || !formik.dirty}
            className={`w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-xl text-white font-bold transition-all shadow-md ${
              loading || !formik.dirty ? "bg-gray-400 cursor-not-allowed" : "bg-[#00B8D9] hover:bg-[#009cb8] hover:shadow-lg active:scale-95"
            }`}
          >
            {loading && <Loader2 className="animate-spin w-5 h-5" />}
            <span>{loading ? "Updating Password..." : "Update Password"}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordForm;


